import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Snowflake, TrendingUp, Megaphone } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import SiteFooter from '@/components/SiteFooter';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const LEVEL_STYLES = {
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  extreme: 'bg-rose-50 text-rose-700 border-rose-200',
};

function LevelChip({ level }) {
  return (
    <span className={`inline-block text-[11px] font-semibold uppercase px-2 py-0.5 rounded border ${LEVEL_STYLES[level] || LEVEL_STYLES.medium}`}>
      {level}
    </span>
  );
}

export default function ChristmasForecastPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API}/airport/christmas-forecast`);
        if (r.ok && !cancel) setData(await r.json());
      } catch { /* static content still renders */ }
      if (!cancel) setLoading(false);
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800" data-testid="christmas-forecast-page">
      <header className="container mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0">
            <AppLogo size={36} className="flex-shrink-0" />
            <span className="heading text-[13px] sm:text-base font-semibold tracking-tight text-slate-900">
              How Busy Is My Airport
            </span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Predictor
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-600 mb-2 flex items-center gap-2">
              <Snowflake className="w-4 h-4" /> Seasonal Forecast · Christmas & New Year 2026–27
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Christmas 2026 Airport Crowd Forecast
            </h1>
            <p className="text-lg text-slate-600">
              Crowd projections for the 16 busiest US airports across the three days that define
              the Christmas–New Year travel window — modeled from scheduled flight density,
              aircraft capacity, and holiday demand patterns.
            </p>
          </div>

          {/* Static editorial — prerendered for crawlers */}
          <section className="mb-10 space-y-4 text-slate-700 leading-relaxed">
            <h2 className="text-2xl font-semibold text-slate-900">Why Christmas crowds behave differently</h2>
            <p>
              Christmas 2026 falls on a <strong>Friday, December 25</strong>, which stretches the
              holiday into a long two-week plateau rather than Thanksgiving's sharp 48-hour spike.
              School calendars release travelers in waves starting around December 18, the heaviest
              outbound day lands on <strong>Wednesday, December 23</strong>, and the return flows
              back across New Year's — peaking on <strong>Sunday, January 3</strong>, the last day
              before most of the country returns to work. Christmas Day itself, like Thanksgiving
              Day, is one of the calmest and cheapest flying days of the season.
            </p>
            <p>
              Two multipliers make Christmas the more treacherous of the winter holidays. First, the
              crowd skews toward <em>once-a-year flyers</em> — more checked bags, slower security
              lanes, longer lines at identical passenger counts. Second, late December is peak winter
              storm season: one ground stop at <Link to="/airports/ord" className="text-blue-600 underline">O'Hare</Link> or{' '}
              <Link to="/airports/den" className="text-blue-600 underline">Denver</Link> cascades
              through every airline's schedule nationwide, and the next morning's rebooking wave
              creates a second peak on top of the holiday peak. That's why our data-backed advice
              for this season emphasizes <strong>resilience over timing</strong>: nonstops where
              possible, morning departures before <Link to="/blog/afternoon-flight-delays-explained" className="text-blue-600 underline">delays cascade</Link>,
              and generous layovers through northern hubs (<Link to="/blog/layover-connection-time-guide" className="text-blue-600 underline">the math here</Link>).
            </p>
            <p>
              The table below comes from the same engine behind our{' '}
              <Link to="/" className="text-blue-600 underline">live predictor</Link> — see the{' '}
              <Link to="/methodology" className="text-blue-600 underline">methodology</Link> for how
              scoring works, and compare with our{' '}
              <Link to="/thanksgiving-2026-forecast" className="text-blue-600 underline">Thanksgiving 2026 forecast</Link>{' '}
              or the full breakdown in{' '}
              <Link to="/blog/thanksgiving-christmas-airport-crowds" className="text-blue-600 underline">
                Thanksgiving vs. Christmas: Which Holiday Actually Wrecks Airports?
              </Link>
            </p>
          </section>

          {data?.national && (
            <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="xmas-national-summary">
              <Card className="border-rose-200 bg-rose-50/50">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 mb-1">Busiest day nationally</p>
                  <p className="text-lg font-bold text-slate-900">{data.national.busiest_day}</p>
                  <p className="text-xs text-slate-500">{data.national.busiest_day_tag}</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 mb-1">Calmest day to fly</p>
                  <p className="text-lg font-bold text-slate-900">{data.national.calmest_day}</p>
                  <p className="text-xs text-slate-500">{data.national.calmest_day_tag}</p>
                </CardContent>
              </Card>
            </div>
          )}

          <Card className="border-slate-200 bg-white mb-10">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <h2 className="heading text-lg font-semibold text-slate-900">Hub-by-hub forecast</h2>
              </div>
              {loading && <p className="text-sm text-slate-500 py-6 text-center">Computing forecast…</p>}
              {!loading && !data && (
                <p className="text-sm text-slate-500 py-6 text-center">Forecast temporarily unavailable — try refreshing.</p>
              )}
              {data?.hubs && (
                <div className="overflow-x-auto" data-testid="xmas-forecast-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                        <th className="py-2 pr-3">Airport</th>
                        <th className="py-2 pr-3">Wed Dec 23 peak</th>
                        <th className="py-2 pr-3">Sun Jan 3 peak</th>
                        <th className="py-2 pr-3">Calmest window (Dec 23)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.hubs.map((h) => {
                        const out = h.days.find((d) => d.tag === 'Peak Outbound');
                        const ret = h.days.find((d) => d.tag === 'Return Sunday');
                        return (
                          <tr key={h.code} data-testid={`xmas-row-${h.code}`}>
                            <td className="py-2.5 pr-3">
                              <Link to={`/airports/${h.code.toLowerCase()}`} className="font-mono font-semibold text-blue-600 hover:underline">
                                {h.code}
                              </Link>
                              <span className="hidden sm:inline text-slate-500"> {h.city}</span>
                            </td>
                            <td className="py-2.5 pr-3 whitespace-nowrap">
                              {out && <>{out.peak_window} <LevelChip level={out.peak_level} /></>}
                            </td>
                            <td className="py-2.5 pr-3 whitespace-nowrap">
                              {ret && <>{ret.peak_window} <LevelChip level={ret.peak_level} /></>}
                            </td>
                            <td className="py-2.5 pr-3 whitespace-nowrap text-emerald-700">{out?.calm_window}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <section className="mb-10 space-y-4 text-slate-700 leading-relaxed">
            <h2 className="text-2xl font-semibold text-slate-900">Playing the season</h2>
            <p>
              <strong>Outbound (Dec 18–24):</strong> fly before December 22 if you can — fares and
              crowds climb daily toward the 23rd. On peak days, morning departures beat afternoons
              twice over: calmer checkpoints and far fewer inherited delays.
            </p>
            <p>
              <strong>Christmas Day and December 26:</strong> the quiet eye of the storm. If your
              family schedule allows a Christmas-morning flight, you'll enjoy the calmest major-hub
              terminals of the month.
            </p>
            <p>
              <strong>The return (Dec 27 – Jan 3):</strong> spread out, but Sunday January 3 is the
              wall — everyone's back to work Monday. Return midweek (Dec 29–30) or push to Tuesday
              January 5 for the calm, cheap "January dead zone" that frequent flyers quietly treasure.
              Whatever you pick, run your exact flight through the{' '}
              <Link to="/" className="text-blue-600 underline">predictor</Link> the day before —
              December is when live delay data matters most.
            </p>
          </section>

          <Card className="border-blue-200 bg-blue-50/50" data-testid="xmas-press-box">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <Megaphone className="w-4 h-4 text-blue-600" />
                <h2 className="heading text-base font-semibold text-slate-900">Journalists & media</h2>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                Free to cite with attribution to <strong>HowBusyIsMyAirport.com</strong>. City-specific
                breakdowns, hour-by-hour charts for any of 331 airports, and interviews available.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline">
                Contact us for data or interviews <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
